import { Menu } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAtom, useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MouseEvent, ReactElement, useCallback, useMemo, useState } from 'react';

import { ROUTES } from '../../../constants/RouteNameMapping';
import { ZINDEX } from '../../../constants/styles';
import { VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { useHasAdditionalNav } from '../../../hooks/useHasAdditionalNav';
import { useIsCollectionOnly } from '../../../hooks/useIsCollectionOnly';
import { useEntityManagementEnabled } from '../../../pages/EntityManagement/hooks/useEntityManagementEnabled';
import { useDefaultPortfolioView } from '../../../pages/PortfolioOverview/hooks/useSyncWithSearchParams';
import { viewsPFJ } from '../../../pages/PortfolioOverview/state/ViewStateJ';
import { useGetDefaultMprView, getShowSeparateMprRoute } from '../../../pages/TempMpr/TempMprWrapper';
import { PermissionKey, PersonaKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { useAccountSwitcher } from '../../../services/hooks/useAccountSwitcher';
import { clientConfigFieldsAtom } from '../../../services/state/AppConfigStateJ';
import { theme } from '../../../theme';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { AccountMenu } from '../../AccountMenu/AccountMenu';
import { CurrentUserAvatar } from '../../Avatar/CurrentUserAvatar';
import { Icon, IconTypes } from '../../Icon';
import { isNavigationOpenAtom } from '../state/NavigationState';
import { AddDealButton } from './AddModal';
import { MenuToggle } from './MenuToggle';
import { NavItem } from './NavItem';

export const MENU_WIDTH = 72;

const Wrapper = styled('nav')`
  display: grid;
  justify-content: center;

  grid-template-rows: auto 1fr auto;
  width: ${MENU_WIDTH}px;
  color: ${theme.colors.primary['0']};
  height: 100dvh;
  padding: 1rem 0;
  background: #232465;
  row-gap: 1rem;
  z-index: ${ZINDEX.NAV_BAR};
`;

const HeaderContentWrapper = styled('div')`
  display: grid;
  grid-template-rows: 34px auto;
  justify-content: center;
  align-items: start;
  width: 100%;
  row-gap: 1rem;
`;

const SettingsWrapper = styled('div')`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const MainMenuWrapper = styled('div')`
  display: grid;
  overflow: auto;
  width: 100%;
  align-content: start;
  @media screen and (min-height: 900px) {
    margin-top: 1rem;
  }
`;

const AvatarWrapper = styled('div')`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding-top: 10px;
  cursor: pointer;
`;

export function Navbar(): ReactElement {
  const permSvc = PermissionService.get();
  const isCollectionOnly = useIsCollectionOnly();
  const { showSourcing, showDocumentHub } = useFlags<MaggieFeatureFlags>();
  const showAdminPanel = permSvc.hasPermission(PermissionKey.canViewAppAdminPanel) && !isCollectionOnly;
  const [isOpen, setIsOpen] = useAtom(isNavigationOpenAtom);
  const hasAdditionalNav = useHasAdditionalNav();
  const isEntityManagementEnabled = useEntityManagementEnabled();
  const showSeparateMprRoute = getShowSeparateMprRoute();
  const defaultMprViewId = useGetDefaultMprView()?.id;
  const defaultPortfolioView = useDefaultPortfolioView();
  const usingConsolidatedDataCollection =
    useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection ?? false;

  // For auditors, use first SHARED view instead of default view
  const isAuditor = PermissionService.get().isAuditor();
  const views = useAtomValue(viewsPFJ);
  const portfolioPath = useMemo(() => {
    if (isAuditor) {
      // Auditors can only access SHARED views
      const firstSharedView = views.find((view) => view.type === VIEW_TYPE.SHARED);
      return firstSharedView ? `${ROUTES.PORTFOLIO}/${firstSharedView.id}` : ROUTES.PORTFOLIO;
    }
    // Regular users use default view or base route
    return defaultPortfolioView ? `${ROUTES.PORTFOLIO}/${defaultPortfolioView.id}` : ROUTES.PORTFOLIO;
  }, [isAuditor, views, defaultPortfolioView]);

  // Initialize account switcher to load tenants and handle auto-switching
  useAccountSwitcher();

  const MAIN_NAV_ITEMS = useMemo(() => {
    if (PermissionService.get().hasPersona(PersonaKey.managedServices)) {
      return [
        {
          icon: <Icon type={IconTypes.KPI_COLORED} color={'rgba(0, 0, 0, 0)'} />,
          text: 'KPIs',
          path: `${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}`,
          showItem: permSvc.hasPermission(PermissionKey.hasDataCollectionModule),
        },
      ];
    }
    return [
      {
        icon: <Icon type={IconTypes.COMPANIES_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Companies',
        path: ROUTES.COMPANIES,
        showItem: !isCollectionOnly && permSvc.hasPermission(PermissionKey.canViewList),
      },
      {
        icon: <Icon type={IconTypes.SOURCING_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Sourcing',
        path: ROUTES.SOURCING,
        showItem: !isCollectionOnly && showSourcing,
      },
      {
        icon: <Icon type={IconTypes.PORTFOLIO_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Portfolio',
        path: portfolioPath,
        showItem: !isCollectionOnly && permSvc.hasPermission(PermissionKey.canViewMetrics),
      },
      {
        icon: <Icon type={IconTypes.PORTFOLIO_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Portfolio',
        path: `${ROUTES.MULTIPERIOD_REPORTING}/${defaultMprViewId}`,
        showItem: !isCollectionOnly && showSeparateMprRoute && typeof defaultMprViewId === 'number',
      },
      {
        icon: <Icon type={IconTypes.ENTITIES_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Entities',
        path: `${ROUTES.ENTITIES}/${ROUTES.ENTITIES_STRUCTURE_CHART}`,
        showItem: !isCollectionOnly && isEntityManagementEnabled,
      },
      {
        icon: <Icon type={IconTypes.FINANCE_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Finance',
        path: `${ROUTES.FINANCE2}/${ROUTES.FINANCE2_OVERVIEW}`,
        showItem: !isCollectionOnly && permSvc.hasPermission(PermissionKey.canViewTransaction),
      },
      {
        icon: <Icon type={IconTypes.DEALBOARD_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Pipeline',
        path: `${ROUTES.DEAL_FLOW}/${ROUTES.DEALS}/`,
        showItem: !isCollectionOnly && permSvc.hasPermission(PermissionKey.canViewDeal),
      },

      {
        icon: <Icon type={IconTypes.REPORT_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'Docs',
        path: ROUTES.DOCS,
        showItem: !isCollectionOnly && showDocumentHub && Boolean(usingConsolidatedDataCollection),
      },
      {
        icon: <Icon type={IconTypes.KPI_COLORED} color={'rgba(0, 0, 0, 0)'} />,
        text: 'KPIs',
        path: usingConsolidatedDataCollection
          ? `${ROUTES.KPI}/${ROUTES.KPI_MANAGE}`
          : `${ROUTES.KPI}/${ROUTES.KPI_REQUESTS}`,
        showItem: permSvc.hasPermission(PermissionKey.hasDataCollectionModule),
      },
    ];
  }, [
    defaultMprViewId,
    portfolioPath,
    permSvc,
    showSeparateMprRoute,
    isCollectionOnly,
    showSourcing,
    showDocumentHub,
    usingConsolidatedDataCollection,
    isEntityManagementEnabled,
  ]);

  const handleOnAdditionalMenuClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, [setIsOpen]);

  return (
    <Wrapper data-testid='navbar'>
      <HeaderContentWrapper>
        {hasAdditionalNav ? <MenuToggle isOpen={isOpen} onClick={handleOnAdditionalMenuClick} /> : <div />}
        {permSvc.hasPermission(PermissionKey.canEditDeal) && <AddDealButton />}
      </HeaderContentWrapper>
      <MainMenuWrapper>
        {MAIN_NAV_ITEMS.map(({ icon, text, path, showItem }) =>
          showItem ? <NavItem key={path} icon={icon} text={text} path={path} /> : null
        )}
      </MainMenuWrapper>
      <SettingsWrapper>
        {showAdminPanel && (
          <NavItem
            key={ROUTES.ADMIN_PANEL}
            icon={<Icon type={IconTypes.ADMIN_PANEL_COLORED} color={'rgba(0, 0, 0, 0)'} />}
            text='Admin Panel'
            path={ROUTES.ADMIN_PANEL}
          />
        )}
        <UserMenu />
      </SettingsWrapper>
    </Wrapper>
  );
}

export function UserMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <AvatarWrapper
        onClick={(e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          handleClick(e);
        }}
      >
        <CurrentUserAvatar size={'medium'} style={{ color: 'black' }} />
      </AvatarWrapper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={(e: MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.stopPropagation();
          handleCloseMenu();
        }}
      >
        <AccountMenu onCloseMenu={handleCloseMenu} />
      </Menu>
    </>
  );
}
